import { Trans, useLingui } from '@lingui/react/macro';
import { useForm } from '@tanstack/react-form';
import { useQueryClient } from '@tanstack/react-query';
import { Mail01 } from '@untitledui/icons/Mail01';
import { useEffect, useState } from 'react';
import { z } from 'zod';

import type { ParticipantProfileDto } from '@/api/core-api/types.gen';

import { EmploymentStatus } from '@/api/core-api/types.gen';
import { Button } from '@/components/base/buttons/button';
import { FormInput } from '@/components/base/form/form-input';
import { ControlledModal } from '@/components/base/modals/modal';
import { AsyncMultiSelect } from '@/components/base/select/async-multi-select';
import { WorkEmailCodeForm } from '@/components/work-email-verification/work-email-code-form';
import { employmentStatusOptions } from '@/constants/profile-demographics-options';
import { queryKeys } from '@/hooks/api';
import { useUpdateProfile } from '@/hooks/api/use-update-profile';
import { requiresEmployerDetails } from '@/routes/participants/-utils/onboarding-prefilled-fields';
import { searchJobTitles } from '@/utils/search-job-titles';
import { setWorkEmailVerificationReturnPathCookie } from '@/utils/work-email-verification-return-cookie';

type ModalStep = 'profile' | 'verification';

type WorkEmailVerificationModalProps = {
  profile: ParticipantProfileDto;
  userId: string;
  returnPath: string;
  onClose: () => void;
};

type ModalContentProps = WorkEmailVerificationModalProps & {
  step: ModalStep;
  setStep: (step: ModalStep) => void;
};

export function WorkEmailVerificationModal({
  profile,
  userId,
  returnPath,
  onClose,
}: WorkEmailVerificationModalProps) {
  const { t } = useLingui();

  const [step, setStep] = useState<ModalStep>(
    Boolean(profile.employment?.email) &&
      profile.employment?.isEmailVerified !== true &&
      profile.workVerificationEmailSent === true
      ? 'verification'
      : 'profile',
  );

  return (
    <ControlledModal
      isOpen
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
      centered
      size="lg"
      title={
        step === 'profile'
          ? t`Verify your work email`
          : t`Enter your verification code`
      }
      className="mx-auto w-full rounded-xl bg-primary p-4 shadow-xl sm:p-6"
    >
      <WorkEmailVerificationModalContent
        key={[
          profile.id,
          profile.employment?.email,
          profile.employment?.isEmailVerified,
          profile.workVerificationEmailSent,
        ].join(':')}
        profile={profile}
        userId={userId}
        returnPath={returnPath}
        onClose={onClose}
        step={step}
        setStep={setStep}
      />
    </ControlledModal>
  );
}

function WorkEmailVerificationModalContent({
  profile,
  userId,
  returnPath,
  onClose,
  step,
  setStep,
}: ModalContentProps) {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const updateProfileMutation = useUpdateProfile();
  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    setWorkEmailVerificationReturnPathCookie(returnPath);
  }, [returnPath]);

  const profileId = profile.id;
  const [workEmail, setWorkEmail] = useState(profile.employment?.email ?? '');
  // True only when saving the profile just triggered a code email. When the
  // modal opens straight into step two the outstanding code may be old, so the
  // resend countdown should not run.
  const [codeJustSent, setCodeJustSent] = useState(false);

  const profileFormSchema = z
    .object({
      employmentStatus: z.enum(EmploymentStatus, {
        message: t`Employment status is required`,
      }),
      companyName: z.string(),
      workEmail: z.string(),
      jobTitle: z
        .object({ id: z.string(), label: z.string() })
        .or(z.undefined()),
    })
    .superRefine(
      ({ employmentStatus, companyName, workEmail, jobTitle }, ctx) => {
        if (!requiresEmployerDetails(employmentStatus)) {
          return;
        }

        const trimmedCompanyName = companyName.trim();
        const trimmedWorkEmail = workEmail.trim();

        if (trimmedCompanyName.length === 0) {
          ctx.addIssue({
            code: 'custom',
            message: t`Company name is required`,
            path: ['companyName'],
          });
        }

        if (!jobTitle?.id) {
          ctx.addIssue({
            code: 'custom',
            message: t`Job title is required`,
            path: ['jobTitle'],
          });
        }

        if (trimmedWorkEmail.length === 0) {
          ctx.addIssue({
            code: 'custom',
            message: t`Work email is required`,
            path: ['workEmail'],
          });
        } else if (
          !z
            .email(t`Enter a valid work email address`)
            .safeParse(trimmedWorkEmail).success
        ) {
          ctx.addIssue({
            code: 'custom',
            message: t`Enter a valid work email address`,
            path: ['workEmail'],
          });
        }
      },
    );

  const form = useForm({
    defaultValues: {
      employmentStatus: profile.employment?.employmentStatus,
      companyName: profile.employment?.company ?? '',
      workEmail: profile.employment?.email ?? '',
      jobTitle: profile.employment?.jobTitleId
        ? {
            id: profile.employment.jobTitleId,
            label: profile.employment.jobTitle ?? '',
          }
        : undefined,
    },
    validators: { onSubmit: profileFormSchema, onChange: profileFormSchema },
    onSubmit: ({ value }) => {
      setServerError(null);
      const isEmployed = requiresEmployerDetails(value.employmentStatus);

      updateProfileMutation.mutate(
        {
          profileId,
          userId,
          updateProfileDto: {
            employment: {
              jobTitle: value.jobTitle?.id ?? null,
              employmentStatus: value.employmentStatus,
              company: isEmployed ? value.companyName.trim() : undefined,
              email: isEmployed ? value.workEmail.trim() : undefined,
              // core-api nulls any employment key the patch omits.
              seniorityLevel: isEmployed
                ? (profile.employment?.seniorityLevel ?? undefined)
                : undefined,
            },
          },
        },
        {
          onSuccess: async () => {
            await queryClient.invalidateQueries({
              queryKey: queryKeys.profile.byUserId(userId),
            });

            if (isEmployed) {
              // The link in the same email still works, so keep the return
              // path cookie fresh for participants who click it instead of
              // typing the code.
              setWorkEmailVerificationReturnPathCookie(returnPath);

              // The server only emails a code when work email or company
              // actually changed. Unchanged saves (common when only a legacy
              // link token remains, or attempts were exhausted) must not start
              // the resend countdown — there'd be nothing in the inbox yet.
              const nextEmail = value.workEmail.trim();
              const nextCompany = value.companyName.trim();
              const codeWasEmailed =
                Boolean(nextEmail) &&
                (profile.employment?.email !== nextEmail ||
                  profile.employment?.company !== nextCompany);

              setWorkEmail(nextEmail);
              setCodeJustSent(codeWasEmailed);
              setStep('verification');
            } else {
              onClose();
            }
          },
          onError: (error) => {
            setServerError(error.message);
          },
        },
      );
    },
  });

  const handleClose = () => {
    onClose();
  };

  return (
    <>
      {step === 'profile' ? (
        <div className="flex w-full flex-col gap-6">
          <div>
            <h2 className="font-heading text-lg font-semibold text-primary">
              <Trans>Tell us about your work</Trans>
            </h2>
            <p className="mt-1 text-sm text-secondary">
              <Trans>
                Verified work details unlock higher-paying professional studies.
              </Trans>
            </p>
          </div>

          {serverError && (
            <p className="text-sm text-error-primary">{serverError}</p>
          )}

          <form
            onSubmit={(e) => {
              e.preventDefault();
              void form.handleSubmit();
            }}
            className="flex flex-col gap-5"
          >
            <FormInput
              type="select"
              field="employmentStatus"
              size="md"
              form={form}
              label={t`Employment status`}
              placeholder={t`Select employment status`}
              items={employmentStatusOptions}
            />

            <form.Subscribe
              selector={(state) =>
                requiresEmployerDetails(state.values.employmentStatus)
              }
              children={(showEmployerFields) =>
                showEmployerFields ? (
                  <div className="flex flex-col gap-5">
                    <FormInput
                      type="text"
                      field="companyName"
                      size="md"
                      form={form}
                      label={t`Company name`}
                      placeholder={t`Company name`}
                    />
                    <form.Field
                      name="jobTitle"
                      children={(field) => (
                        <AsyncMultiSelect
                          label={t`Job title`}
                          value={field.state.value ? [field.state.value] : []}
                          onChange={(items) => {
                            const next = items[0];
                            field.setValue(
                              next
                                ? { id: next.id, label: next.label ?? '' }
                                : undefined,
                            );
                          }}
                          onSearch={searchJobTitles}
                          placeholder={t`Search job titles...`}
                          maxSelections={1}
                          minSearchChars={0}
                          size="md"
                        />
                      )}
                    />
                    <FormInput
                      type="email"
                      field="workEmail"
                      size="md"
                      form={form}
                      label={t`Work email`}
                      placeholder={t`name@company.com`}
                      hint={t`We'll send one email to this address to verify it, and never anything else. Study invitations still go to your personal email.`}
                    />
                  </div>
                ) : null
              }
            />

            <div className="flex flex-col gap-3 pt-2">
              <Button
                type="submit"
                color="primary"
                size="lg"
                className="w-full"
                isLoading={updateProfileMutation.isPending}
                isDisabled={updateProfileMutation.isPending}
              >
                <Trans>Continue</Trans>
              </Button>
              <Button
                type="button"
                color="secondary"
                size="lg"
                className="w-full"
                onClick={handleClose}
              >
                <Trans>Cancel</Trans>
              </Button>
            </div>
          </form>
        </div>
      ) : (
        <div className="flex w-full flex-col gap-6">
          <div className="flex flex-col items-center text-center">
            <div className="flex size-12 items-center justify-center rounded-full bg-brand-primary">
              <Mail01 className="size-6 text-brand-secondary" />
            </div>
            <h2 className="mt-4 font-heading text-lg font-semibold text-primary">
              <Trans>Enter your verification code</Trans>
            </h2>
            <p className="mt-2 text-sm text-secondary">
              <Trans>Once verified, you can apply to this project.</Trans>
            </p>
          </div>

          <WorkEmailCodeForm
            profileId={profileId}
            workEmail={workEmail}
            codeJustSent={codeJustSent}
            onVerified={() => {
              void queryClient
                .invalidateQueries({
                  queryKey: queryKeys.profile.byUserId(userId),
                })
                .then(onClose);
            }}
          />

          <Button
            type="button"
            color="secondary"
            size="lg"
            className="w-full"
            onClick={handleClose}
          >
            <Trans>Cancel</Trans>
          </Button>
        </div>
      )}
    </>
  );
}
